import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Button, Space, Spin, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { colors } from '../../styles/colors';
import { TEMPLATE_DETAIL_FIELDS, type TemplateDetail } from './model';
import { printChrome, printFieldOf, printVersionOf, type PrintField } from './printModel';

/**
 * E34 · "Practice — Worksheets — Print view" (Q-paper-2 A, the founder 2026-10-03; cc-homework, chapter "Worksheets —
 * Templates & library (portal, E34)", page 2; route `/worksheets/:templateId/print?version=`): the printable rendering
 * of a worksheet template version, for the client who does it on paper. The title; each section's instructions; every
 * field as its label with blank space to write — a single-line field a rule, a long text a block of lines, a 0–N scale
 * a row of numbers to circle, single and multiple choice their options as tick boxes, a date a blank line, a checkbox a
 * tick box. A header line "Name: ______  Date: ______" is BLANK: the client's name is never pre-printed (privacy). No
 * signature line — a consent is never printed. The browser prints it or saves it as PDF; nothing is stored.
 *
 * Opened from the Print action of a library row, of the Builder, and of the client hub's worksheet row (which passes the
 * version the assignment pinned: `worksheetTemplate(templateId, version)`). Only a PAPER-ELIGIBLE template (unscored and
 * not a consent — `paperEligible`) prints; a scored worksheet or a consent shows the line the library gives instead,
 * even when the address is typed in.
 *
 * Q-print-1 A (2026-10-03) BRANDING (a CopingCard LIBRARY sheet only; a practice's own, imported or copied sheet carries no
 * logo, just the footer text "Prepared with CopingCard"): page 1 opens with the CopingCard logo (left) and the signed-in therapist's practice
 * name (right); EVERY page ends with a slim footer (the logo mark, copingcard.com, the worksheet's name, "Page X of Y")
 * and the return line "Done? Take a photo of this sheet in the CopingCard app…". All of that chrome is in the WORKSHEET's
 * language (`printChrome`), not the portal's. Page numbers come from the print CSS (`@page` bottom-right margin box with
 * `counter(page)` / `counter(pages)`); a browser without margin boxes prints everything but the number.
 *
 * Print styles: the browser's own paper (A4 or Letter), the portal chrome hidden, each section kept together, a fixed
 * footer repeated on every page (a table footer reserves its room so the sheet never runs under it). i18n worksheets.print.*.
 */
const INK = colors['--dark-gray'];

const rule = (short: boolean): CSSProperties => ({
  borderBottom: `1px solid ${INK}`,
  height: 30,
  width: short ? 200 : '100%',
});

const tickBox: CSSProperties = {
  display: 'inline-block',
  width: 16,
  height: 16,
  border: `1.5px solid ${INK}`,
  borderRadius: 2,
  flex: '0 0 auto',
};

const Answer = ({ f }: { f: PrintField }) => {
  switch (f.kind) {
    case 'rule':
      return <div style={rule(f.short)} data-testid="print-rule" />;
    case 'lines':
      return (
        <div data-testid="print-lines">
          {Array.from({ length: f.count }, (_, i) => (
            <div key={i} style={rule(false)} />
          ))}
        </div>
      );
    case 'scale':
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="print-scale">
          {f.values.map((v) => (
            <span
              key={v}
              style={{
                width: 30,
                height: 30,
                lineHeight: '30px',
                textAlign: 'center',
                border: `1px solid ${INK}`,
                borderRadius: '50%',
              }}
            >
              {v}
            </span>
          ))}
        </div>
      );
    case 'ticks':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="print-ticks">
          {f.options.map((o) => (
            <div key={o} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={tickBox} />
              <span>{o}</span>
            </div>
          ))}
        </div>
      );
    case 'tick':
      return <span style={tickBox} data-testid="print-tick" />;
  }
};

export const WorksheetPrintView = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [search] = useSearchParams();
  const version = printVersionOf(search.get('version'));

  const { data, isFetching, isError } = useCustom<TemplateDetail>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: Boolean(templateId), retry: false },
    meta: {
      operation: 'worksheetTemplate',
      fields: TEMPLATE_DETAIL_FIELDS,
      variables: {
        templateId: { name: 'templateId', type: 'ID!', value: templateId },
        version: { name: 'version', type: 'Int', value: version },
      },
    },
  });
  const ws = data?.data ?? null;
  // the signed-in therapist's practice (the same `practice` query the subscription page reads) — its NAME heads page 1
  const { data: practiceData } = useCustom<{ practiceName?: string | null }>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { retry: false },
    meta: { operation: 'practice', fields: ['practiceName'] },
  });
  const chrome = ws ? printChrome(ws, practiceData?.data?.practiceName) : null;
  const eligible = !!ws && ws.paperEligible && !ws.signatureRequired;

  return (
    <div data-testid="worksheet-print" style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* the sheet prints as itself: the menu, the header, the footer and the buttons stay out of the PDF */}
      <style>{`
        @page {
          margin: 16mm 16mm 22mm;
          ${
            chrome
              ? `@bottom-right { content: ${JSON.stringify(chrome.pageBefore)} counter(page) ${JSON.stringify(chrome.pageBetween)} counter(pages) ${JSON.stringify(chrome.pageAfter)}; font-size: 10px; color: #555; }`
              : ''
          }
        }
        @media print {
          .ant-layout-sider, .ant-layout-header, .ant-layout-footer, [data-print-hide],
          .ant-notification, .ant-message, .ant-float-btn-group, .ant-float-btn { display: none !important; }
          .ant-layout, .ant-layout-content { margin: 0 !important; padding: 0 !important; background: transparent !important; }
          [data-print-sheet] { max-width: none !important; }
          [data-print-section] { break-inside: avoid; page-break-inside: avoid; }
          [data-print-footer] { position: fixed; bottom: 0; left: 0; right: 0; }
          table[data-print-frame] { width: 100%; }
        }
      `}</style>

      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap data-print-hide>
        <Button onClick={() => navigate(-1)} data-testid="worksheet-print-back">
          ← {t('worksheets.print.back')}
        </Button>
        <Button type="primary" onClick={() => window.print()} disabled={!eligible} data-testid="worksheet-print-button">
          {t('worksheets.response.print')}
        </Button>
      </Space>

      {isFetching && !ws ? <Spin /> : null}
      {isError ? (
        <Alert type="error" showIcon message={t('worksheets.print.loadFailed')} data-print-hide />
      ) : null}
      {!isFetching && !isError && !ws ? (
        <Alert
          type="warning"
          showIcon
          message={t('worksheets.print.notFound')}
          data-testid="worksheet-print-not-found"
          data-print-hide
        />
      ) : null}
      {ws && !eligible ? (
        <Alert
          type="info"
          showIcon
          message={t('worksheets.library.printNotEligible')}
          data-testid="worksheet-print-not-eligible"
          data-print-hide
        />
      ) : null}

      {ws && eligible ? (
        <div data-print-sheet data-testid="worksheet-print-sheet" lang={chrome?.language}>
          <table data-print-frame style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: 0, verticalAlign: 'top' }}>
          <Typography.Paragraph type="secondary" data-print-hide style={{ fontSize: 12 }}>
            {t('worksheets.print.note')}
          </Typography.Paragraph>

          {/* page 1 only (normal flow): the CopingCard logo, the practice's name */}
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}
            data-testid="worksheet-print-brand"
          >
            {chrome?.branded ? (
              <img src="/logo-print.svg" alt="CopingCard" style={{ height: 28 }} data-testid="worksheet-print-logo" />
            ) : (
              <span />
            )}
            {chrome?.practiceLine ? (
              <span style={{ fontSize: 13, color: INK, textAlign: 'right' }} data-testid="worksheet-print-practice">
                {chrome.practiceLine}
              </span>
            ) : null}
          </div>

          <Typography.Title level={2} style={{ marginBottom: 16 }} data-testid="worksheet-print-title">
            {ws.name}
          </Typography.Title>
          {/* the client's name is never pre-printed: a blank to fill in */}
          <div style={{ display: 'flex', gap: 24, marginBottom: 24 }} data-testid="worksheet-print-header">
            <div style={{ flex: '1 1 60%', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <span>{t('worksheets.print.nameLine')}:</span>
              <div style={{ ...rule(false), flex: 1 }} />
            </div>
            <div style={{ flex: '1 1 30%', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <span>{t('worksheets.print.dateLine')}:</span>
              <div style={{ ...rule(false), flex: 1 }} />
            </div>
          </div>

          {ws.sections.map((s) => (
            <section
              key={s.sectionId}
              data-print-section
              style={{ marginBottom: 28 }}
              data-testid={`worksheet-print-section-${s.sectionId}`}
            >
              {s.title ? (
                <Typography.Title level={4} style={{ marginBottom: 6 }}>
                  {s.title}
                </Typography.Title>
              ) : null}
              {s.text ? (
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{s.text}</Typography.Paragraph>
              ) : null}
              <Space direction="vertical" size={18} style={{ width: '100%' }}>
                {s.fields.map((f) => {
                  const printed = printFieldOf(f);
                  return (
                    <div key={f.fieldId} data-testid={`worksheet-print-field-${f.fieldId}`}>
                      {printed.kind === 'tick' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Answer f={printed} />
                          <span>{f.label}</span>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontWeight: 600, marginBottom: 6 }}>{f.label}</div>
                          <Answer f={printed} />
                        </>
                      )}
                    </div>
                  );
                })}
              </Space>
            </section>
          ))}

                </td>
              </tr>
            </tbody>
            {/* the footer's room on every page: a table footer repeats, so the content stops above the fixed footer */}
            <tfoot>
              <tr>
                <td style={{ padding: 0 }}>
                  <div style={{ height: 64 }} />
                </td>
              </tr>
            </tfoot>
          </table>

          <div
            data-print-footer
            style={{ fontSize: 11, color: INK, background: '#fff', borderTop: `1px solid ${INK}`, paddingTop: 4 }}
            data-testid="worksheet-print-footer"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {chrome?.branded ? (
                <>
                  <img src="/logo-mark-print.svg" alt="" style={{ height: 14 }} data-testid="worksheet-print-mark" />
                  <span data-testid="worksheet-print-site">{chrome.site}</span>
                </>
              ) : (
                <span data-testid="worksheet-print-prepared">{chrome?.preparedWith}</span>
              )}
              <span>·</span>
              <span data-testid="worksheet-print-footer-name">{chrome?.footerName}</span>
            </div>
            <div data-testid="worksheet-print-return">{chrome?.returnLine}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
